/**
 * Decision 0084 (build map `docs/design/project-pages/build-map.md` §1.1, §3.2, §4, §8.1): project and agent pages.
 *
 * Pages are ordinary Minga documents at fixed paths; the paths are the contract. This module names them, classifies a
 * path, holds the per-page byte caps for context pinning and the templates the bootstrap job and the web share.
 *
 * Pure and dependency-free (no `effect` import), like `doc-path.ts`.
 */
import { docPathKey } from "./doc-path"

/** What a page at a fixed path is (§1.1). `proposals/*` drafts are not pages. */
export type PageKind =
  | "overview"
  | "status"
  | "decisions"
  | "role"
  | "mandate"
  | "assumptions"
  | "notebook"
  | "guideline"
  /** Decision 0086 (§5): `areas/<slug>/status.md`, the area lead agent's status page. */
  | "area_status"

/** The agent pages under `agents/<handle>/`. */
export type AgentPageName = "role" | "mandate" | "assumptions" | "notebook"

export const PROJECT_PAGES = {
  overview: "project/overview.md",
  status: "project/status.md",
  /** Decision 0084 addendum (2026-10-14): the decision log, appended by people and agents alike. */
  decisions: "project/decisions.md",
} as const satisfies Record<"overview" | "status" | "decisions", string>

/** The project-level page kinds (one fixed path each). */
export type ProjectPageKind = keyof typeof PROJECT_PAGES

/** How we work, for everyone including workers (`guidelines/<topic>.md`). */
export const GUIDELINES_DIR = "guidelines/"
/** `propose_page` drafts: `proposals/<page path>`. Never pinned; people edit the real page instead. */
export const PROPOSALS_DIR = "proposals/"
/** The folder of one agent's pages. */
export const AGENTS_DIR = "agents/"
/** Decision 0086 (§5): the folder of the areas' pages (`areas/<slug>/status.md`). */
export const AREAS_DIR = "areas/"

/** `areas/<slug>/status.md`: an area's status, kept by its lead agent (decision 0086 §5). */
export const areaStatusPath = (slug: string): string => `${AREAS_DIR}${slug}/status.md`

const AREA_STATUS_PAGE = /^areas\/([^/]+)\/status\.md$/

/** `agents/<handle>/<page>.md`. */
export const agentPagePath = (handle: string, page: AgentPageName): string =>
  `${AGENTS_DIR}${handle}/${page}.md`

const AGENT_PAGE = /^agents\/([^/]+)\/(role|mandate|assumptions|notebook)\.md$/

/**
 * The page at `path`, or null (case-insensitive, like `docPathKey`). `handle` is the `agents/<handle>/` segment as
 * written in `path` (null for project pages and guidelines). `area` is the `areas/<slug>/` segment of an area status
 * page as written (decision 0086; absent otherwise). `agents/<h>/playbooks/*` and `proposals/*` are not pages.
 */
export const pageKindOf = (path: string): { kind: PageKind; handle: string | null; area?: string } | null => {
  const key = docPathKey(path)
  if (key === PROJECT_PAGES.overview) return { kind: "overview", handle: null }
  if (key === PROJECT_PAGES.status) return { kind: "status", handle: null }
  if (key === PROJECT_PAGES.decisions) return { kind: "decisions", handle: null }
  if (key.startsWith(GUIDELINES_DIR) && key.length > GUIDELINES_DIR.length + 3 && key.endsWith(".md"))
    return { kind: "guideline", handle: null }
  if (AREA_STATUS_PAGE.test(key)) return { kind: "area_status", handle: null, area: path.split("/")[1] ?? "" }
  const m = AGENT_PAGE.exec(key)
  if (m) return { kind: m[2] as AgentPageName, handle: path.split("/")[1] ?? null }
  return null
}

/** Is `path` a `propose_page` draft (`proposals/…`)? Case-insensitive. */
export const isProposalPath = (path: string): boolean => docPathKey(path).startsWith(PROPOSALS_DIR)

/** The draft path for a page: `proposals/<path>`. */
export const proposalPathOf = (pagePath: string): string => `${PROPOSALS_DIR}${pagePath}`

/** The page a draft proposes (`proposals/project/overview.md` → `project/overview.md`), or null for other paths. */
export const proposedPagePathOf = (draftPath: string): string | null =>
  isProposalPath(draftPath) ? draftPath.slice(PROPOSALS_DIR.length) : null

/** Person-owned pages: agents never write them, they `propose_page` (§2.1). */
export const LOCKED_PAGE_KINDS: ReadonlySet<PageKind> = new Set<PageKind>(["overview", "role", "mandate"])

const KB = 1024

/** Per-page byte caps for pinned context (UTF-8 bytes, §3.2). A guideline's cap is per page; see GUIDELINES_TOTAL_CAP. */
export const PAGE_CAPS: Readonly<Record<PageKind, number>> = {
  overview: 6 * KB,
  status: 4 * KB,
  decisions: 4 * KB,
  role: 4 * KB,
  mandate: 3 * KB,
  assumptions: 2 * KB,
  notebook: 5 * KB,
  guideline: 4 * KB,
  area_status: 4 * KB,
}

/** The pinned pages' own budget, separate from the linked-document budget (§3.2). Validators use `worker`. A lead turn
 * also pins the decision log (0084 addendum), hence 28 KB. */
export const PAGE_BUDGET = { turn: 28_672, worker: 16_384 } as const

/** All guidelines pinned for one worker or validator together (§3.3). */
export const GUIDELINES_TOTAL_CAP = 6_144

/** `project/status.md`'s `##` headings, exactly these, in this order (§4). */
export const STATUS_HEADINGS = ["Where we are", "Next", "Waiting on people", "Risks"] as const

/** `areas/<slug>/status.md` (decision 0086 §5): the {@link STATUS_HEADINGS}, every section empty. */
export const AREA_STATUS_TEMPLATE = (areaName: string): string =>
  [`# Status: ${areaName}`, "", ...STATUS_HEADINGS.flatMap((s) => [`## ${s}`, "None.", ""])].join("\n")

/** `mandate.md`'s `##` sections, always in this order (§1.1, 0072 §4.4). */
export const MANDATE_SECTIONS = [
  "Responsibility",
  "Success",
  "Authority",
  "Resources",
  "Reserved decisions",
  "Reporting",
] as const

/** `assumptions.md`'s sections: the mandate's without Authority, Resources and Reserved decisions (§1.1). */
export const ASSUMPTION_SECTIONS = ["Responsibility", "Success", "Reporting"] as const

/** The overview of a project with an empty brief (§1.3). */
export const OVERVIEW_TEMPLATE = (project: string): string =>
  [
    `# ${project}`,
    "",
    "## Vision",
    "What this project is for and what changes when it succeeds.",
    "",
    "## For whom, and their loop",
    "Who it is for (by name when known), and what they repeatedly do or need from it.",
    "",
    "## Scope",
    "What is in.",
    "",
    "## Non-goals",
    "What is deliberately out.",
    "",
  ].join("\n")

/**
 * A new agent's mandate: the headings, nothing Instructed yet (§1.1, §1.3). Decision 0159: people read it as
 * "Responsibilities" (the file and the agents' word stay `mandate`); `legacy` is the heading written before 0159, so a
 * template saved unchanged then still reads as empty.
 */
export const MANDATE_TEMPLATE = (handle: string, project: string, legacy = false): string =>
  [
    `# ${legacy ? "Mandate" : "Responsibilities"}: @${handle} (${project})`,
    "",
    "Every line here comes from a person and ends with its source: (Instructed: <name>, <date>, <ref or link>).",
    "",
    ...MANDATE_SECTIONS.flatMap((s) => [`## ${s}`, "None yet.", ""]),
  ].join("\n")

/** An agent's working assumptions (written by the agent, §1.1). */
export const ASSUMPTIONS_TEMPLATE = (handle: string): string =>
  [
    `# Assumptions: @${handle}`,
    "",
    "Working assumptions; they never grant authority. Each line ends (Assumed: <why, ref>). A person confirms a line by moving it into mandate.md.",
    "",
    ...ASSUMPTION_SECTIONS.flatMap((s) => [`## ${s}`, "None.", ""]),
  ].join("\n")

/** `project/status.md` with its fixed headings, every section empty (§4). */
export const STATUS_TEMPLATE: string = [
  "# Status",
  "",
  ...STATUS_HEADINGS.flatMap((s) => [`## ${s}`, "None.", ""]),
].join("\n")

/**
 * `project/decisions.md` (decision 0084 addendum): what was settled, newest last, one entry per decision. Agents and
 * people append; nothing is locked. The lead pins it; workers see it listed.
 */
export const DECISIONS_TEMPLATE: string = [
  "# Decisions",
  "",
  "What was settled, newest last. One entry per decision:",
  "",
  "- YYYY-MM-DD · the decision · Why: the reason · Who: who decided · the card, chat or item it came from (DIC-12, P-3, a link)",
  "",
  "## Log",
  "",
  "None yet.",
  "",
].join("\n")

/** `agents/<h>/role.md` from a charter body (§1.3). The mission stays a record field. */
export const ROLE_TEMPLATE = (handle: string, body: string): string =>
  `# Role: @${handle}\n\n${body.trim()}\n`

/** The pages job's `preview` on the overview it seeds when there was no brief (the template, not content). */
export const OVERVIEW_TEMPLATE_PREVIEW = "The overview template (there was no brief yet)"
/** The pages job's `preview` on the decision log it seeds (the template). */
export const DECISIONS_TEMPLATE_PREVIEW = "The decision log: date, decision, why, who, and where it came from"

/**
 * Polish 2026-10-23 §6: a page that is still the pages job's seeded template (written by `system:pages` with one of
 * the template previews), so the app shows its empty state instead of the template's placeholder lines as content.
 * A moved brief (also by the pages job) is real content and is not a template.
 */
export const isSeededTemplate = (page: {
  readonly createdBy: { readonly _tag: string; readonly process?: string }
  readonly preview: string | null
}): boolean =>
  page.createdBy._tag === "system" &&
  page.createdBy.process === "pages" &&
  (page.preview === OVERVIEW_TEMPLATE_PREVIEW || page.preview === DECISIONS_TEMPLATE_PREVIEW)
